// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * Le CATALOGUE des champs d'une fiche : ce qui existe, son libellé, sa section,
 * son type de saisie.
 *
 * ── Pourquoi partagé ────────────────────────────────────────────────────────
 *
 * Ce catalogue se règle désormais à deux endroits — l'atelier du manuel de
 * prélèvement, et la rubrique « Manuel » du module Qualité, d'où le catalogue
 * se pilote depuis le 15/08/2026. Les deux écrans activent les mêmes champs,
 * leur donnent le même type et les rangent dans les mêmes sections : ils
 * doivent lire la MÊME liste.
 *
 * Deux copies auraient tenu jusqu'au premier champ ajouté d'un côté : de
 * l'autre, la clé aurait paru sans libellé (ou pas du tout), sans qu'aucune
 * erreur ne se déclenche — la valeur en base est parfaitement valide. C'est le
 * défaut que `backend/scripts/audit-champs-fiche.js` traque depuis le 07/08,
 * et le meilleur moyen de ne pas le rejouer est de n'avoir qu'une liste.
 *
 * ⚠️ Le SEED du paramétrage (`backend/src/index.ts`, `FIELD_CATALOG`) reste une
 * liste distincte, parce que le backend ne lit pas `shared/`. L'audit compare
 * les deux : clé, section, type et appartenance au socle doivent concorder.
 */

export type FieldType =
  | 'text' | 'area' | 'list' | 'number'
  | 'recipient' | 'famille' | 'lieu' | 'syn'
  | 'url' | 'document' | 'date'
  // MAN-5 — deux types multi-valués stockés dans UNE chaîne (aucune migration
  // de modèle) : « tags » = étiquettes séparées par « · », « bullets » = une
  // ligne par puce. Le stockage reste lisible tel quel dans un export CSV.
  | 'tags' | 'bullets';

export interface TypeOption {
  value: FieldType;
  label: string;
}

export interface FieldDef {
  key: string;
  label: string;
  section: string;
  type: FieldType;
  core?: boolean;
  mono?: boolean;
  prixST?: boolean;
  noEdit?: boolean;
  hideForPublic?: boolean;
  custom?: boolean;
  /** Unité affichée à côté de la valeur, jamais stockée avec elle — la mêler
   *  au nombre est ce qui avait rendu les prix incomparables entre eux. */
  unite?: string;
  /** Le champ se lit comme un AVERTISSEMENT : rendu en rouge, encadré, jamais
   *  noyé dans la grille des autres valeurs. Le livret v4.1 imprime ces
   *  remarques en rouge — les reprendre en noir leur ôterait ce qui les
   *  distingue, et c'est précisément ce qu'un sous-traitant doit voir en
   *  premier (« congeler dans les 4 h » n'est pas une ligne de tarif). */
  alerte?: boolean;
}
export const FIELD_CATALOG: FieldDef[] = [
  { key: 'libelle',       label: 'Libellé',                             section: 'Identité',             type: 'text',      core: true },
  { key: 'famille',       label: 'Famille',                             section: 'Identité',             type: 'famille',   core: true },
  { key: 'sousFamille',   label: 'Sous-famille',                        section: 'Identité',             type: 'list' },
  { key: 'synonymes',     label: 'Synonymes',                           section: 'Identité',             type: 'syn' },
  { key: 'cibles',        label: 'Cibles recherchées',                  section: 'Identité',             type: 'syn' },
  // 07/09/2026 — la nature de l'examen et son régime génétique (listes fermées du modèle).
  { key: 'nature',        label: 'Nature de l’examen',                  section: 'Identité',             type: 'list' },
  { key: 'genetique',     label: 'Régime génétique',                    section: 'Identité',             type: 'list' },
  /* LISTE et non texte libre (RQ, 19/08/2026) : en libre, « Sang veineux »,
     « Sang » et « Sang total » coexistaient pour la même idée. La liste se
     règle au paramétrage (clé `milieu`), donc s'enrichit sans toucher au code.
     PAS redondant avec `echantillon` : le milieu est ce qu'on PRÉLÈVE (sang
     veineux), l'échantillon ce qu'on ANALYSE (le sérum qu'on en tire). */
  /* `milieux` (PLURIEL) — ce qu'on PRÉLÈVE au patient. Un même examen accepte
     souvent plusieurs prélèvements selon la question clinique : la culture de
     BK se fait sur expectoration, urines, LCR ou liquide de ponction.
     ⚠️ REMPLACE `milieu` (retiré du catalogue le 13/09/2026, RQ) — l'ancien
     champ mono-valué obligeait à écrire « Expectoration / Urines / LCR /
     Ponction » dans une chaîne : on ne pouvait donc JAMAIS répondre à « quels
     examens se font sur LCR ? ». La migration des 322 fiches concernées était
     déjà complète en base (vérifié le 13/09) ; seul le champ lui-même restait
     à retirer du modèle. */
  { key: 'milieux',       label: 'Milieu(x) biologique(s) prélevé(s)',  section: 'Pré-analytique',       type: 'list' },
  { key: 'echantillon',   label: 'Échantillon',                         section: 'Pré-analytique',       type: 'list' },
  { key: 'recipient',     label: 'Récipient (normalisé)',               section: 'Pré-analytique',       type: 'recipient', core: true },
  { key: 'recipientDero', label: 'Récipient dérogatoire',              section: 'Pré-analytique',       type: 'text' },
  { key: 'volumeMin',     label: 'Volume minimal',                      section: 'Pré-analytique',       type: 'text',      mono: true },
  { key: 'prep',          label: 'Préparation du patient',              section: 'Pré-analytique',       type: 'area' },
  { key: 'conditions',    label: 'Conditions particulières',            section: 'Pré-analytique',       type: 'area' },
  { key: 'transport',     label: "Transport / délai d'acheminement",    section: 'Pré-analytique',       type: 'list' },
  { key: 'renseignements',label: 'Renseignements cliniques',            section: 'Pré-analytique',       type: 'area' },
  { key: 'congele',       label: 'Congelé −20 °C',                      section: 'Conservation',         type: 'list' },
  { key: 'refrigere',     label: 'Réfrigéré 2–8 °C',                   section: 'Conservation',         type: 'list' },
  { key: 'ambiante',      label: 'Température ambiante',               section: 'Conservation',         type: 'list' },
  { key: 'delaiAjout',    label: "Délai d'ajout d'analyse",            section: 'Conservation',         type: 'list' },
  { key: 'lieu',          label: "Lieu d'analyse",                      section: 'Réalisation & rendu',  type: 'lieu' },
  /* `delaiReal` et `delaiRendu` QUITTENT le catalogue (Ines, 19/10/2026) :
     deux textes libres pour une notion qui est une DONNÉE. Le délai de rendu
     est `delaiRenduHeures` (un maximum, en heures), le délai de réalisation
     est remplacé par `joursRealisation` — tous deux saisis dans le module
     Qualité. Les colonnes restent en base comme trace de la reprise, déclarées
     dans `FICHE_HORS_CATALOGUE` (manuel/src/lib/champsFiche.ts). */
  { key: 'indications',   label: 'Indications',                         section: 'Réalisation & rendu',  type: 'area' },
  { key: 'valeursRef',    label: 'Valeurs de référence',               section: 'Réalisation & rendu',  type: 'text' },
  /* « Principe scientifique » et non « Principe de la méthode » (13/09/2026) :
     ce texte libre décrit le PRINCIPE ANALYTIQUE pour le lecteur du catalogue
     (« Immunodosage par chimiluminescence ») — il ne dit pas SUR QUEL AUTOMATE
     l'examen tourne ici. Ce dernier est `principeMethode`, calculé depuis les
     techniques rattachées, juste en dessous : deux champs presque homonymes
     avaient fini par se confondre (« il faut que ce soit tiré des techniques,
     non ? » — RQ, alors que c'est déjà le cas pour l'AUTRE champ). */
  { key: 'principe',      label: 'Principe scientifique (pour le catalogue)', section: 'Réalisation & rendu',  type: 'text' },
  // `principeMethode` (technique × automate) N'EST PAS ICI, volontairement :
  // c'est un CALCUL (concaténation des techniques rattachées), pas un
  // attribut du schéma — l'y déclarer ferait échouer l'audit champs-fiche
  // (« écriture refusée par Strapi », aucun tel attribut n'existe). Il se
  // montre dans l'Avant/Après comme une ligne à part, hors de cette boucle —
  // voir `Referentiel.tsx` (13/09/2026).
  { key: 'cotation',      label: 'Cotation (nombre de B)',              section: 'Facturation',          type: 'text',      mono: true },
  { key: 'cnam',          label: 'Code CNAM / HN',                      section: 'Facturation',          type: 'text',      mono: true },
  { key: 'sousTraitance', label: 'Proposé à la sous-traitance',         section: 'Facturation',          type: 'text',      hideForPublic: true },
  { key: 'prixST',        label: 'Prix HT',                            section: 'Facturation',          type: 'number',    mono: true, prixST: true, noEdit: true, unite: 'TND' },
  { key: 'remarqueImportante', label: 'Remarque importante',            section: 'Facturation',          type: 'area',      alerte: true },
  { key: 'accredite',     label: 'Accrédité',                           section: 'Accréditation',        type: 'list' },
  { key: 'porteeAccred',  label: "Portée d'accréditation",              section: 'Accréditation',        type: 'area' },
  { key: 'dateAccred',    label: "Date d'accréditation",                section: 'Accréditation',        type: 'date' },
  /* ── « Patients » (Ines, 16/10/2026) ──────────────────────────────────────
     Le catalogue public n'est plus la fiche technique entière lue par tout le
     monde : c'est une LISTE COURTE d'examens, avec un contenu écrit pour le
     patient. Trois champs, et pas un sous-ensemble des champs techniques :
     « À jeun 12 h, tube fluoré, 2 mL » ne s'adapte pas en le tronquant, on
     écrit l'autre texte. Le reste de la fiche (préparation du préleveur,
     tubes, conservation, valeurs de référence, indications) est réservé aux
     Correspondants connectés — défaut « correspondant » dans
     `backend/src/visibilite-champs.ts`.
     `visiblePatients` est une liste « Oui »/« Non » comme `accredite`, pour
     se régler par la machinerie générique (édition, lot, diffusion, tableur) ;
     « Oui » et rien d'autre ouvre la fiche au public, côté SERVEUR. */
  { key: 'visiblePatients',    label: 'Visible des patients (liste courte)', section: 'Patients',  type: 'list' },
  { key: 'patientUtilite',     label: 'À quoi sert cet examen (pour le patient)', section: 'Patients', type: 'area' },
  // « bullets » : une consigne par ligne, rendue en liste — le patient lit des
  // étapes, pas un paragraphe (Ines, 17/10/2026 : « structuré, ce serait un plus »).
  { key: 'patientPreparation', label: 'Comment me préparer (pour le patient)',   section: 'Patients', type: 'bullets' },
];

export const FIELD_SECTION_ORDER = ['Identité', 'Patients', 'Pré-analytique', 'Conservation', 'Réalisation & rendu', 'Facturation', 'Accréditation'];

export const CONFIGURABLE_TYPE_OPTIONS: TypeOption[] = [
  { value: 'text',     label: 'Texte libre' },
  { value: 'area',     label: 'Texte long' },
  { value: 'number',   label: 'Chiffre' },
  { value: 'list',     label: 'Liste déroulante' },
  // MAN-5 — deux types multi-valués. « Étiquettes » : plusieurs valeurs courtes
  // côte à côte (mots-clés, sigles). « Liste à puces » : une consigne par ligne.
  { value: 'tags',     label: 'Étiquettes' },
  { value: 'bullets',  label: 'Liste à puces' },
  { value: 'url',      label: 'Lien URL' },
  { value: 'document', label: 'Document à charger' },
  { value: 'date',     label: 'Date' },
];

export const SPECIAL_TYPES: FieldType[] = ['recipient', 'famille', 'lieu', 'syn'];
